import { useQuery } from "@tanstack/react-query";

import {
  IAdminDeviceHistory,
  IAdminPatientDevice,
} from "../../../@types/admin/AdminDevice";
import { IAdminFirmware } from "../../../@types/admin/AdminFirmware";
import {
  firmwareService,
  patientDevicesService,
} from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";

/**
 * Every firmware record. The endpoint wraps its rows in `{ data: [...] }`,
 * unlike the other migrated list endpoints — unwrapped here so callers get a
 * plain array.
 */
export function useAdminFirmware() {
  const { data, isLoading, isError } = useQuery<IAdminFirmware[]>({
    queryKey: ["admin-firmware"],
    queryFn: async () => {
      const response = ensureOk(await firmwareService.getFirmware());
      return response?.data ?? [];
    },
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { firmware: data ?? [], isLoading, isError };
}

/** Devices — all of them, or one patient's. Backs `PatientDevicesPanel`. */
export function useAdminPatientDevices(patientId?: number) {
  const { data, isLoading, isError } = useQuery<IAdminPatientDevice[]>({
    queryKey: ["admin-patient-devices", patientId ?? "all"],
    queryFn: async () =>
      ensureOk(await patientDevicesService.getDevices(patientId)),
    refetchOnMount: "always",
  });

  return { devices: data ?? [], isLoading, isError };
}

/**
 * One device's detail and patient history. Idle until a device is chosen,
 * matching Retool's history modal, which only ran its query once opened.
 */
export function useAdminDeviceHistory(identifier: string | undefined) {
  const { data, isLoading, isError } = useQuery<IAdminDeviceHistory>({
    queryKey: ["admin-device-history", identifier],
    queryFn: async () =>
      ensureOk(await patientDevicesService.getDeviceHistory(identifier!)),
    enabled: Boolean(identifier),
    refetchOnMount: "always",
  });

  return { deviceHistory: data, isLoading, isError };
}
